import { defineCollection } from "@rebasepro/cms-types";
import { country, moment } from "../words";
import gamePlayersCollection from "./game-players";
import type { CollectionConfig } from "@rebasepro/types";
import gameSongsCollection from "./game-songs";

// Every party game played on the cloud, one row each: when, which room, how many played, what music, whether it was
// finished. backend/src/games.ts fills it from the event log (songbuzz_events: game_start, game_end), the past ones
// included. Its rounds are Game songs.
const gamesCollection = defineCollection({
    name: "Games",
    singularName: "Game",
    slug: "games",
    table: "songbuzz_games",
    description: "Every party game played, from start to end. Built from the event log, so it goes back a year.",
    securityRules: [
        { operation: "select", roles: ["admin"] },
        { operation: "delete", roles: ["admin"] },
    ],
    properties: {
        id: { name: "Game", type: "number", isId: "manual", columnType: "bigint", admin: { readOnly: true } },
        startedAt: { name: "Started", type: "date", validation: { required: true }, admin: { readOnly: true } },
        room: { name: "Room", type: "string", admin: { readOnly: true } },
        players: { name: "Players", type: "number", columnType: "integer", admin: { readOnly: true } },
        // the names their phones sent (backend/src/games.ts nameGame), by place once it ended; games from before the
        // phones sent them (3 Nov 2026) have none
        playerNames: { name: "Who played", type: "string", admin: { readOnly: true } },
        remotePlayers: { name: "Playing from elsewhere", type: "number", columnType: "integer", admin: { readOnly: true } },
        phoneHost: { name: "Hosted on a phone", type: "boolean", admin: { readOnly: true } },
        music: { name: "Music", type: "string", admin: { readOnly: true, Preview: "../../admin/src/previews/Words", customProps: { say: "mix", list: true } } },
        country: { name: "Country", type: "string", admin: { readOnly: true, Preview: "../../admin/src/previews/Words", customProps: { say: "country" } } },
        level: { name: "Difficulty", type: "string", admin: { readOnly: true, Preview: "../../admin/src/previews/Words", customProps: { say: "level" } } },
        roundTypes: { name: "Rounds", type: "string", admin: { readOnly: true, Preview: "../../admin/src/previews/Words", customProps: { say: "round", list: true } } },
        songsPlanned: { name: "Songs planned", type: "number", columnType: "integer", admin: { readOnly: true } },
        songsPlayed: { name: "Songs played", type: "number", columnType: "integer", admin: { readOnly: true } },
        finished: { name: "Finished", type: "boolean", defaultValue: false, validation: { required: true }, admin: { readOnly: true } },
        endedAt: { name: "Ended", type: "date", admin: { readOnly: true } },
        minutes: { name: "Minutes", type: "number", columnType: "real", admin: { readOnly: true, format: { maximumFractionDigits: 0 } } },
        topScore: { name: "Winning score", type: "number", columnType: "integer", admin: { readOnly: true } },
        margin: { name: "Won by (points)", type: "number", columnType: "integer", admin: { readOnly: true } },
        lang: { name: "Presenter's language", type: "string", admin: { readOnly: true, Preview: "../../admin/src/previews/Words", customProps: { say: "language" } } },
        device: { name: "Shown on", type: "string", admin: { readOnly: true, Preview: "../../admin/src/previews/Words", customProps: { say: "device" } } },
        results: {
            name: "Results", type: "relation",
            relation: { kind: "via", cardinality: "many", target: (): CollectionConfig => gamePlayersCollection, relationName: "results", joinPath: [{ table: "songbuzz_game_players", on: { from: "id", to: "game_id" } }] },
            admin: { readOnly: true },
        },
        songs: {
            name: "Songs", type: "relation",
            // typed, so the two collections' types don't each wait on the other
            relation: { kind: "hasMany", target: (): CollectionConfig => gameSongsCollection, relationName: "songs", foreignKeyOnTarget: "game_id" },
        },
    },
    indexes: [{ on: ["room", "startedAt"], reason: "games.ts finds the game a round or an end belongs to by its room and start" }],
    admin: {
        icon: "PartyPopper",
        group: "Games",
        openEntityMode: "side_panel",
        sort: ["startedAt", "desc"],
        display: {
            // when it was played leads: a list column for it is the first one a narrow screen drops
            title: ({ entity }) => {
                const n = entity.values.players ?? 0;
                return [moment(entity.values.startedAt), entity.values.room || "No room", `${n} ${n === 1 ? "player" : "players"}`, country(entity.values.country)].filter(Boolean).join(" · ");
            },
            // how it went, then who played, or for a game from before the phones said so, its music (the pages send lists
            // joined with commas)
            subtitle: ({ entity }) => {
                const v = entity.values;
                const songs = `${v.songsPlayed ?? 0} of ${v.songsPlanned ?? "?"} songs`;
                const recent = Date.now() - new Date(String(v.startedAt)).getTime() < 3 * 3600e3;
                const how = v.finished ? "Finished" : v.endedAt ? `Left after ${songs}` : `${recent ? "Playing" : "Never ended"} · ${songs}`;
                return [how, v.playerNames || (v.music ? String(v.music).split(",").join(", ") : "")].filter(Boolean).join(" · ");
            },
            status: ({ entity }) => entity.values.finished ? "Finished" : entity.values.endedAt ? "Left early" : undefined,
            date: "startedAt",
        },
        listProperties: ["songsPlayed", "minutes", "topScore"],
        form: {
            sections: [
                { key: "game", properties: ["room", "startedAt", "endedAt", "finished", "minutes", "players", "playerNames", "remotePlayers", "phoneHost", "device"] },
                { key: "setup", title: "How it was set up", properties: ["music", "country", "lang", "level", "roundTypes", "songsPlanned", "songsPlayed"] },
                { key: "score", title: "Scores", properties: ["topScore", "margin", "results", "songs"] },
                { key: "ids", title: "Behind the scenes", collapsed: true, properties: ["id"] },
            ],
        },
    },
});

export default gamesCollection;
